import { z } from 'zod';

import { HTTP_STATUS } from '@/common/http-status';

import type { PiiType } from '@bazak/pii';

type SensitiveDataTypeKey =
  | 'email'
  | 'creditCard'
  | 'phoneNumber'
  | 'ssn'
  | 'passport'
  | 'iban'
  | 'identityCard'
  | 'taxId'
  | 'secrets'
  | 'bankAccount';

/** The chat server's verdict on a turn it masked; CORS-exposed by bazak-mastra. */
export const PII_RESULT_HEADER = 'X-Pii-Result';

const piiItemSchema = z.object({
  // Listed here, not imported: a value import from `@bazak/pii` ships the detector to the browser.
  type: z.enum([
    'card',
    'iban',
    'email',
    'secret',
    'phone',
    'idNumber',
    'passport',
    'ssn',
    'taxId',
    'bankAccount',
  ]),
  ruleId: z.string(),
  /** The value with all but its last characters replaced by `•`, one per character. */
  masked: z.string(),
  subtype: z.string().optional(),
});

const piiBlockedSchema = z.object({
  kind: z.literal('pii'),
  result: z.literal('blocked'),
  items: z.array(piiItemSchema).min(1),
});

const piiNeedsConfirmationSchema = piiBlockedSchema.extend({
  result: z.literal('needs_confirmation'),
});

const piiRefusalSchema = z.discriminatedUnion('result', [
  piiBlockedSchema,
  piiNeedsConfirmationSchema,
]);

const piiMaskedSchema = z.object({
  kind: z.literal('pii'),
  result: z.literal('masked'),
  items: z.array(piiItemSchema).min(1),
  /** The newest user message as the server saved it, values replaced by labels like `[PHONE]`. */
  text: z.string(),
  /** Every user message the server masked, by id: an earlier unanswered one is resent and masked too. */
  maskedMessages: z.array(z.object({ id: z.string(), text: z.string() })).optional(),
});

// `/generate` checks one id-less message whose masked text is already `text`, so `maskedMessages`,
// which the web schema keys by id, is not read.
const generatePiiMaskSchema = piiMaskedSchema.omit({ maskedMessages: true });

export type PiiItem = z.infer<typeof piiItemSchema>;
export type PiiBlocked = z.infer<typeof piiBlockedSchema>;
export type PiiNeedsConfirmation = z.infer<typeof piiNeedsConfirmationSchema>;
/** The server held the turn back: blocked, or until the guest confirms a warning. */
export type PiiRefusal = z.infer<typeof piiRefusalSchema>;
export type PiiMasked = z.infer<typeof piiMaskedSchema>;
export type GeneratePiiMask = z.infer<typeof generatePiiMaskSchema>;
export type PiiResult = PiiRefusal | PiiMasked;
/** The guest's answer to a warning, sent on the message as `metadata.piiDecision`. */
export type PiiDecision = 'send_anyway' | 'not_personal_data';

export const PII_TYPE_LABEL: Record<PiiType, SensitiveDataTypeKey> = {
  card: 'creditCard',
  iban: 'iban',
  email: 'email',
  secret: 'secrets',
  phone: 'phoneNumber',
  idNumber: 'identityCard',
  passport: 'passport',
  ssn: 'ssn',
  taxId: 'taxId',
  bankAccount: 'bankAccount',
};

/** Metadata only — never the value or the message text. */
export function piiEventProperties({
  item,
  messageLength,
  itemCount,
}: {
  item: PiiItem;
  messageLength?: number;
  itemCount?: number;
}) {
  return {
    category: PII_TYPE_LABEL[item.type],
    ruleId: item.ruleId,
    direction: 'input',
    detectedSubtype: item.subtype,
    valueLength: item.masked.length,
    messageLength,
    itemCount,
  };
}

export class PiiRefusalError extends Error {
  public readonly result: PiiRefusal;
  public readonly messageId: string | undefined;

  public constructor(result: PiiRefusal, messageId?: string) {
    super('pii_refused');
    this.name = 'PiiRefusalError';
    this.result = result;
    this.messageId = messageId;
  }
}

/** A server that predates `maskedMessages` reports only the newest message's text. */
export function maskedMessagesOf({
  result,
  newestMessageId,
}: {
  result: PiiMasked;
  newestMessageId: string | undefined;
}): { id: string; text: string }[] {
  if (result.maskedMessages) {
    return result.maskedMessages;
  }
  return newestMessageId ? [{ id: newestMessageId, text: result.text }] : [];
}

export async function readPiiRefusal(response: Response): Promise<PiiRefusal | null> {
  if (response.status !== HTTP_STATUS.UNPROCESSABLE_ENTITY) {
    return null;
  }
  try {
    return piiRefusalSchema.safeParse(await response.clone().json()).data ?? null;
  } catch {
    return null;
  }
}

export function readGeneratePiiMask(value: unknown): GeneratePiiMask | undefined {
  return generatePiiMaskSchema.safeParse(value).data;
}

export function readPiiMask(response: Response): PiiMasked | null {
  const header = response.headers.get(PII_RESULT_HEADER);
  if (!header) {
    return null;
  }
  try {
    return piiMaskedSchema.safeParse(JSON.parse(decodeURIComponent(header))).data ?? null;
  } catch {
    return null;
  }
}
